/**
 * PipelineFunnelStrip — at-a-glance deal funnel above the pipeline table.
 *
 * Renders the four canonical funnel stages (lib/dealFunnel.ts) with live
 * counts and each stage's share of the active pipeline, plus the `passed`
 * disposition at the edge. Clicking a stage toggles the table's stage filter.
 * Pencil register: token surfaces only, no decorative color.
 */

import React from 'react';
import {
  DEAL_STAGE_ORDER,
  DEAL_STAGE_LABELS,
  type CanonicalDealStage,
} from '../../lib/dealFunnel';
import { cn } from '../../lib/utils';

interface PipelineFunnelStripProps {
  counts: Record<CanonicalDealStage, number>;
  activeStage: string | null;
  onStageSelect: (stage: CanonicalDealStage | null) => void;
}

export function PipelineFunnelStrip({ counts, activeStage, onStageSelect }: PipelineFunnelStripProps) {
  const activeTotal = DEAL_STAGE_ORDER.reduce((sum, stage) => sum + counts[stage], 0);

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-3">
      <div className="grid flex-1 grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border sm:grid-cols-4">
        {DEAL_STAGE_ORDER.map((stage) => {
          const isActive = activeStage === stage;
          const share = activeTotal > 0 ? counts[stage] / activeTotal : 0;
          return (
            <button
              key={stage}
              type="button"
              aria-pressed={isActive}
              onClick={() => onStageSelect(isActive ? null : stage)}
              className={cn(
                'group flex flex-col gap-1 px-4 py-3 text-left transition-colors',
                isActive ? 'bg-secondary' : 'bg-card hover:bg-muted/60'
              )}
            >
              <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
                {DEAL_STAGE_LABELS[stage]}
              </span>
              <span className="text-2xl font-semibold tabular-nums tracking-tight text-foreground">
                {counts[stage]}
              </span>
              <span className="h-1 w-full overflow-hidden rounded-full bg-secondary">
                <span
                  className="block h-full rounded-full bg-foreground transition-[width] duration-500"
                  style={{ width: `${Math.round(share * 100)}%` }}
                />
              </span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        aria-pressed={activeStage === 'passed'}
        onClick={() => onStageSelect(activeStage === 'passed' ? null : 'passed')}
        className={cn(
          'flex items-center justify-between gap-3 rounded-lg border border-border px-4 py-3 text-left transition-colors sm:w-32 sm:flex-col sm:items-start sm:justify-start sm:gap-1',
          activeStage === 'passed' ? 'bg-secondary' : 'bg-card hover:bg-muted/60'
        )}
      >
        <span className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
          {DEAL_STAGE_LABELS.passed}
        </span>
        <span className="text-2xl font-semibold tabular-nums tracking-tight text-muted-foreground">
          {counts.passed}
        </span>
      </button>
    </div>
  );
}
